<template>
  <section class="powered-section" aria-labelledby="powered-heading">
    <div class="powered-header">
      <div
        class="powered-icon"
        v-html="LAPTOP_ICON_SVG"
        aria-hidden="true"
      ></div>
      <div class="powered-heading-row">
        <h2 id="powered-heading" class="powered-title">Powered by Eddy</h2>
        <p class="powered-intro">
          Eddy supports both Makercart and Makercrate—from finding an activity
          to preparing it for classroom delivery.
        </p>
      </div>
    </div>

    <div ref="flowRef" class="eddy-flow">
      <article
        v-for="step in eddySteps"
        :key="step.id"
        :ref="(el) => setStepRef(step.id, el)"
        :class="['eddy-step', `eddy-step--${step.id}`]"
      >
        <h3 class="eddy-step__title">{{ step.title }}</h3>
        <p class="eddy-step__description">{{ step.description }}</p>
        <div class="eddy-step__preview">
          <picture>
            <source
              media="(max-width: 768px)"
              :srcset="step.previewMobile"
            />
            <img
              :src="step.preview"
              :alt="step.previewAlt"
              class="eddy-step__preview-image"
              width="572"
              height="254"
              @load="updateArrows"
            />
          </picture>
        </div>
      </article>
      <svg
        v-show="showArrows"
        class="eddy-arrows"
        :width="flowSize.w"
        :height="flowSize.h"
        :viewBox="`0 0 ${flowSize.w} ${flowSize.h}`"
        preserveAspectRatio="xMinYMin meet"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <clipPath
            id="eddy-arrow-clip"
            clipPathUnits="userSpaceOnUse"
            clip-rule="evenodd"
          >
            <path :d="arrowClipPath" fill-rule="evenodd" />
          </clipPath>
        </defs>
        <g clip-path="url(#eddy-arrow-clip)">
          <path
            v-if="arrow1Path"
            :d="arrow1Path"
            class="eddy-arrows__path"
          />
          <path
            v-if="arrow2Path"
            :d="arrow2Path"
            class="eddy-arrows__path"
          />
        </g>
        <polygon
          v-if="arrow1Head"
          :points="arrow1Head"
          fill="#e00069"
        />
        <polygon
          v-if="arrow2Head"
          :points="arrow2Head"
          fill="#e00069"
        />
      </svg>
    </div>

    <p class="powered-footer">
      Everything educators need to move from activity selection to classroom
      implementation.
    </p>
    <div class="powered-cta">
      <ActionButton
        variant="primary"
        size="large"
        type="button"
        @click="router.push({ name: 'MakerCartPage' })"
      >
        Explore Activities on Eddy
        <template #icon-right>
          <ArrowShortRightIcon color="currentColor" />
        </template>
      </ActionButton>
    </div>
  </section>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import ActionButton from "@/components/common/ActionButton.vue";
import { ArrowShortRightIcon } from "@/components/icons";

const router = useRouter();
const ARROW_BREAKPOINT = 1100;
const STROKE_WIDTH = 8;
const HEAD_WIDTH = 46;
const HEAD_HEIGHT = 40;
const HEAD_GAP = 4;
const RADIUS_MAX = 100;
const MIN_HORIZONTAL = 120;
const MIN_VERTICAL = 80;
const CARD_RADIUS = 24;

const flowRef = ref(null);
const stepRefs = {};
const showArrows = ref(false);
const arrow1Path = ref("");
const arrow2Path = ref("");
const arrow1Head = ref("");
const arrow2Head = ref("");
const arrowClipPath = ref("");
const flowSize = reactive({ w: 0, h: 0 });

const setStepRef = (id, el) => {
  stepRefs[id] = el;
};

const relativeRect = (el, parent) => {
  const parentBox = parent.getBoundingClientRect();
  const box = el.getBoundingClientRect();
  return {
    top: box.top - parentBox.top,
    left: box.left - parentBox.left,
    right: box.right - parentBox.left,
    bottom: box.bottom - parentBox.top,
    width: box.width,
    height: box.height,
  };
};

const roundedRectPath = (x, y, w, h, r) => {
  const radius = Math.min(r, w / 2, h / 2);
  return [
    `M ${x + radius} ${y}`,
    `H ${x + w - radius}`,
    `A ${radius} ${radius} 0 0 1 ${x + w} ${y + radius}`,
    `V ${y + h - radius}`,
    `A ${radius} ${radius} 0 0 1 ${x + w - radius} ${y + h}`,
    `H ${x + radius}`,
    `A ${radius} ${radius} 0 0 1 ${x} ${y + h - radius}`,
    `V ${y + radius}`,
    `A ${radius} ${radius} 0 0 1 ${x + radius} ${y}`,
    "Z",
  ].join(" ");
};

const elbowPath = (x1, y1, x2, y2) => {
  const dx = Math.abs(x2 - x1);
  const dy = Math.max(y2 - y1, 0);
  const radius = Math.min(RADIUS_MAX, dx * 0.42, dy * 0.7);

  if (radius < 24) {
    return "";
  }

  if (x2 >= x1) {
    return `M ${x1} ${y1} H ${x2 - radius} A ${radius} ${radius} 0 0 1 ${x2} ${
      y1 + radius
    } V ${y2}`;
  }

  return `M ${x1} ${y1} H ${x2 + radius} A ${radius} ${radius} 0 0 0 ${x2} ${
    y1 + radius
  } V ${y2}`;
};

const arrowHeadPoints = (x, y) => {
  const half = HEAD_WIDTH / 2;
  return [
    `${x - half},${y - HEAD_HEIGHT}`,
    `${x + half},${y - HEAD_HEIGHT}`,
    `${x},${y}`,
  ].join(" ");
};

const horizontalHitsCard = (y, xStart, xEnd, card) => {
  const minX = Math.min(xStart, xEnd);
  const maxX = Math.max(xStart, xEnd);
  const pad = STROKE_WIDTH;
  return (
    y > card.top + pad &&
    y < card.bottom - pad &&
    maxX > card.left + pad &&
    minX < card.right - pad
  );
};

const updateArrows = () => {
  const flow = flowRef.value;
  const step1 = stepRefs[1];
  const step2 = stepRefs[2];
  const step3 = stepRefs[3];

  if (!flow || !step1 || !step2 || !step3) {
    showArrows.value = false;
    return;
  }

  const width = flow.clientWidth;
  flowSize.w = width;
  flowSize.h = flow.clientHeight;

  if (window.matchMedia(`(max-width: ${ARROW_BREAKPOINT}px)`).matches) {
    showArrows.value = false;
    arrow1Path.value = "";
    arrow2Path.value = "";
    arrow1Head.value = "";
    arrow2Head.value = "";
    arrowClipPath.value = "";
    return;
  }

  const s1 = relativeRect(step1, flow);
  const s2 = relativeRect(step2, flow);
  const s3 = relativeRect(step3, flow);
  const preview1 = step1.querySelector(".eddy-step__preview");
  const preview2 = step2.querySelector(".eddy-step__preview");
  const p1 = preview1 ? relativeRect(preview1, flow) : s1;
  const p2 = preview2 ? relativeRect(preview2, flow) : s2;

  arrowClipPath.value = [
    `M 0 0 H ${width} V ${flowSize.h} H 0 Z`,
    roundedRectPath(s1.left, s1.top, s1.width, s1.height, CARD_RADIUS),
    roundedRectPath(s2.left, s2.top, s2.width, s2.height, CARD_RADIUS),
    roundedRectPath(s3.left, s3.top, s3.width, s3.height, CARD_RADIUS),
  ].join(" ");

  const a1StartX = s1.right;
  const a1StartY = p1.top + STROKE_WIDTH / 2;
  const a1EndX = s2.left + s2.width * 0.52;
  const a1TipY = s2.top - HEAD_GAP;
  const a1EndY = a1TipY - HEAD_HEIGHT;

  const a2StartX = s2.left;
  const a2StartY = p2.top + STROKE_WIDTH / 2;
  const a2EndX = s3.left + s3.width * 0.5;
  const a2TipY = s3.top - HEAD_GAP;
  const a2EndY = a2TipY - HEAD_HEIGHT;

  const canDrawFirst =
    a1EndX - a1StartX >= MIN_HORIZONTAL &&
    a1EndY - a1StartY >= MIN_VERTICAL &&
    !horizontalHitsCard(a1StartY, a1StartX, a1EndX, s2) &&
    !horizontalHitsCard(a1StartY, a1StartX, a1EndX, s3);

  const canDrawSecond =
    a2StartX - a2EndX >= MIN_HORIZONTAL &&
    a2EndY - a2StartY >= MIN_VERTICAL &&
    !horizontalHitsCard(a2StartY, a2StartX, a2EndX, s1) &&
    !horizontalHitsCard(a2StartY, a2StartX, a2EndX, s3);

  const path1 = canDrawFirst ? elbowPath(a1StartX, a1StartY, a1EndX, a1EndY) : "";
  const path2 = canDrawSecond
    ? elbowPath(a2StartX, a2StartY, a2EndX, a2EndY)
    : "";

  arrow1Path.value = path1;
  arrow2Path.value = path2;
  arrow1Head.value = path1 ? arrowHeadPoints(a1EndX, a1TipY) : "";
  arrow2Head.value = path2 ? arrowHeadPoints(a2EndX, a2TipY) : "";
  showArrows.value = Boolean(path1 || path2);
};

let resizeObserver = null;
let mediaQuery = null;

onMounted(() => {
  nextTick(() => {
    updateArrows();
    mediaQuery = window.matchMedia(`(max-width: ${ARROW_BREAKPOINT}px)`);
    mediaQuery.addEventListener("change", updateArrows);
    window.addEventListener("resize", updateArrows);
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    resizeObserver = new ResizeObserver(() => {
      updateArrows();
    });
    if (flowRef.value) {
      resizeObserver.observe(flowRef.value);
    }
    Object.values(stepRefs).forEach((el) => {
      if (el) {
        resizeObserver.observe(el);
      }
    });
  });
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
  if (mediaQuery) {
    mediaQuery.removeEventListener("change", updateArrows);
  }
  window.removeEventListener("resize", updateArrows);
});

const LAPTOP_ICON_SVG = `<svg width="68" height="51" viewBox="0 0 68 51" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M0.666687 44C0.666687 42.159 2.15907 40.6666 4.00002 40.6666H64C65.841 40.6666 67.3334 42.159 67.3334 44V47.3333C67.3334 49.1742 65.841 50.6666 64 50.6666H4.00002C2.15907 50.6666 0.666687 49.1742 0.666687 47.3333V44Z" fill="#E00069"/>
  <path d="M64 40.6666V40.9049C63.9999 42.6142 62.5077 43.9995 60.667 43.9996H7.33301C5.49228 43.9995 4.00011 42.6142 4 40.9049V40.6666H64ZM20.5664 0.666626C22.133 0.666626 23.4358 1.91968 24.6777 2.87463C25.3986 3.42885 26.324 3.76226 27.333 3.76233H40.667C41.676 3.76226 42.6014 3.42885 43.3223 2.87463C44.5642 1.91968 45.867 0.666626 47.4336 0.666626H60.667C62.5078 0.66679 64 2.05298 64 3.76233V35.6666H4V3.76233C4 2.05298 5.49221 0.666789 7.33301 0.666626H20.5664Z" fill="#E00069"/>
</svg>`;

const eddySteps = [
  {
    id: 1,
    title: "Step 1: Explore Activities",
    description: "Browse activities for your Makercart or Makercrate.",
    preview: "/images/maker-cart/get-started/eddy-preview-explore.png",
    previewMobile: "/images/maker-cart/get-started/explore-activities.png",
    previewAlt: "Eddy activity catalog with filters and activity cards",
  },
  {
    id: 2,
    title: "Step 2: Preview the Details",
    description:
      "See the activity overview, learning outcomes and materials required.",
    preview: "/images/maker-cart/get-started/eddy-preview-details.png",
    previewMobile: "/images/maker-cart/get-started/preview-the-details.png",
    previewAlt: "Eddy activity detail with overview and add to Makercart",
  },
  {
    id: 3,
    title: "Step 3: Access Teaching Resources",
    description: "Open the lesson plan and supporting instructional materials.",
    preview: "/images/maker-cart/get-started/eddy-preview-resources.png",
    previewMobile:
      "/images/maker-cart/get-started/access-teaching-resources.png",
    previewAlt: "Eddy lesson plan with learning objectives and materials",
  },
];
</script>

<style scoped>
.powered-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding: 40px 0;
}

.powered-header {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.powered-icon {
  display: flex;
  justify-content: left;
  width: 100%;
  height: 80px;
}

.powered-heading-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
}

.powered-title {
  flex: 1;
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-display-small);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-display-small);
  color: var(--blue-dark-2);
}

.powered-intro {
  flex: 0 0 628px;
  max-width: 628px;
  margin: 0;
  text-align: right;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-large);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-title-large);
  color: var(--black-body-text);
}

.eddy-flow {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 40px;
  row-gap: clamp(220px, 21cqi, 269px);
  width: 100%;
  overflow: visible;
  container-type: inline-size;
}

.eddy-step {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-self: start;
  gap: 16px;
  height: fit-content;
  padding: 24px;
  background-color: var(--white);
  border: 2px solid var(--blue-light-1);
  border-radius: 24px;
  box-sizing: border-box;
}

.eddy-step--1 {
  grid-column: 1;
  grid-row: 1;
}

.eddy-step--2 {
  grid-column: 2;
  grid-row: 1 / span 2;
  margin-top: clamp(260px, 27.6cqi, 354px);
}

.eddy-step--3 {
  grid-column: 1;
  grid-row: 2;
}

.eddy-step__title {
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-display-small);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-display-small);
  color: var(--pink-dark-1);
}

.eddy-step__description {
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-headline-small);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-headline-small);
  color: var(--black-body-text);
}

.eddy-step__preview {
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
}

.eddy-step__preview picture {
  display: block;
  width: 100%;
}

.eddy-step__preview-image {
  display: block;
  width: 100%;
  height: auto;
}

.eddy-arrows {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  pointer-events: none;
  z-index: 2;
  overflow: visible;
}

.eddy-arrows__path {
  fill: none;
  stroke: var(--primary-pink);
  stroke-width: 8;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.powered-footer {
  margin: 0;
  text-align: center;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-headline-large);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-headline-large);
  color: var(--blue-dark-2);
}

.powered-cta {
  display: flex;
  justify-content: center;
}

@media (max-width: 1200px) {
  .powered-intro {
    flex: 1;
    max-width: none;
  }

  .eddy-step__title {
    font-size: 30px;
    line-height: 36px;
  }
}

@media (max-width: 1100px) {
  .eddy-flow {
    display: flex;
    flex-direction: column;
    gap: 24px;
    row-gap: 24px;
  }

  .eddy-step--1,
  .eddy-step--2,
  .eddy-step--3 {
    margin-top: 0;
  }

  .eddy-arrows {
    display: none;
  }
}

@media (max-width: 992px) {
  .powered-heading-row {
    flex-direction: column;
    gap: 16px;
  }

  .powered-intro {
    text-align: left;
  }

  .powered-footer {
    font-size: var(--fs-xxl);
    line-height: 36px;
  }
}

@media (max-width: 768px) {
  .powered-section {
    gap: 24px;
    padding: 16px 0;
  }

  .powered-header {
    gap: 24px;
  }

  .powered-icon {
    height: 40px;
  }

  .powered-icon :deep(svg) {
    width: auto;
    height: 40px;
  }

  .powered-heading-row {
    gap: 16px;
  }

  .powered-title {
    font-size: var(--font-size-title-large);
    line-height: var(--line-height-title-large);
  }

  .powered-intro {
    font-size: var(--font-size-body-large);
    line-height: var(--line-height-body-large);
    font-weight: var(--font-weight-regular);
    letter-spacing: 0.024px;
  }

  .eddy-step {
    gap: 8px;
    padding: 24px;
  }

  .eddy-step__title {
    font-size: var(--font-size-headline-medium);
    line-height: var(--line-height-headline-medium);
  }

  .eddy-step__description {
    font-size: var(--font-size-title-big);
    line-height: var(--line-height-title-big);
    letter-spacing: 0.03px;
  }

  .eddy-step__preview {
    border-radius: 12px;
  }

  .powered-footer {
    font-size: var(--font-size-title-big);
    line-height: var(--line-height-title-big);
    letter-spacing: 0.03px;
  }
}
</style>
